import * as GlobalTypes from '../types'

/**
 * Get the appropriate icon for a source type
 */
export const getSourceTypeIcon = (type: GlobalTypes.SourceTypes): string => {
  switch (type) {
    case GlobalTypes.SourceTypes.rss:
      return '📰'
    case GlobalTypes.SourceTypes.twitter:
    case GlobalTypes.SourceTypes.twitterList:
      return '🐦'
    case GlobalTypes.SourceTypes.facebook:
      return '📘'
    case GlobalTypes.SourceTypes.youtube:
      return '📺'
    case GlobalTypes.SourceTypes.native:
      return '✍️'
    case GlobalTypes.SourceTypes.scraper:
      return '🔄'
    default:
      return '📄'
  }
}

/**
 * Format source type for display
 */
export const formatSourceType = (type: GlobalTypes.SourceTypes): string => {
  switch (type) {
    case GlobalTypes.SourceTypes.rss:
      return 'RSS Feed'
    case GlobalTypes.SourceTypes.twitter:
      return 'Twitter'
    case GlobalTypes.SourceTypes.twitterList:
      return 'Twitter List'
    case GlobalTypes.SourceTypes.facebook:
      return 'Facebook'
    case GlobalTypes.SourceTypes.youtube:
      return 'YouTube'
    case GlobalTypes.SourceTypes.native:
      return 'Manual Entry'
    case GlobalTypes.SourceTypes.scraper:
      return 'Web Scraper'
    default:
      return type
  }
}

/**
 * Extract domain from URL for display
 */
export const getSourceDomain = (url: string | null): string | null => {
  if (!url) return null

  try {
    // Handle URLs that might not have protocol
    const urlToProcess = url.startsWith('http') ? url : `https://${url}`
    const urlObj = new URL(urlToProcess)

    // Remove www. prefix for cleaner display
    return urlObj.hostname.replace(/^www\./, '')
  } catch {
    // If URL parsing fails, try to extract domain manually
    const match = url.match(/(?:https?:\/\/)?(?:www\.)?([^\/\s]+)/i)
    return match ? match[1] : url.length > 30 ? `${url.substring(0, 30)}...` : url
  }
}

/**
 * Get color class for source type
 */
export const getSourceTypeColor = (type: GlobalTypes.SourceTypes): string => {
  switch (type) {
    case GlobalTypes.SourceTypes.rss:
      return 'source-type--rss'
    case GlobalTypes.SourceTypes.twitter:
    case GlobalTypes.SourceTypes.twitterList:
      return 'source-type--twitter'
    case GlobalTypes.SourceTypes.facebook:
      return 'source-type--facebook'
    case GlobalTypes.SourceTypes.youtube:
      return 'source-type--youtube'
    case GlobalTypes.SourceTypes.native:
      return 'source-type--manual'
    case GlobalTypes.SourceTypes.scraper:
      return 'source-type--scraper'
    default:
      return 'source-type--default'
  }
}

/**
 * Generate quality score (placeholder for future implementation)
 */
export const getSourceQualityScore = (source: GlobalTypes.Source): number => {
  // Placeholder algorithm - in real implementation this would be based on
  // actual metrics like engagement, update frequency, reliability, etc.
  const factors = [
    source.name.length > 5 ? 1 : 0, // Has reasonable name length
    source.category ? 1 : 0, // Has category
    source.sub_category ? 1 : 0, // Has subcategory
  ]

  const score = Math.min(5, Math.max(1, factors.reduce((a, b) => a + b, 0) + 2))
  return score
}

/**
 * Format quality score as stars
 */
export const formatQualityStars = (score: number): string => {
  const fullStars = Math.floor(score)
  const hasHalfStar = score % 1 >= 0.5
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0)

  return '★'.repeat(fullStars) + (hasHalfStar ? '☆' : '') + '☆'.repeat(emptyStars)
}

/**
 * Check if source matches search term
 */
export const sourceMatchesSearch = (source: GlobalTypes.Source, searchTerm: string): boolean => {
  if (!searchTerm) return true

  const term = searchTerm.toLowerCase()
  const searchableText = [source.name, source.identifier, source.category, source.sub_category]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

  return searchableText.includes(term)
}

/**
 * Get relevance score for sorting
 */
export const getSearchRelevanceScore = (source: GlobalTypes.Source, searchTerm: string): number => {
  if (!searchTerm) return 0

  const term = searchTerm.toLowerCase()
  let score = 0

  // Name matches get highest score
  if (source.name.toLowerCase().includes(term)) {
    score += source.name.toLowerCase().startsWith(term) ? 100 : 50
  }

  // Category matches get medium score
  if (source.category && source.category.toLowerCase().includes(term)) {
    score += 25
  }

  // Identifier/URL matches get lower score
  if (source.identifier.toLowerCase().includes(term)) {
    score += 10
  }

  return score
}
